
  import Combobox from "@github/combobox-nav";
  import { computePosition, autoUpdate, offset, flip, shift, size } from "@floating-ui/dom";

  export default {
    mounted() {
      const wrapper = this.el;
      const fieldset = wrapper.closest("fieldset");
      const isMultiple = wrapper.dataset.multiple === "true";

      this._wrapper = wrapper;
      this._fieldset = fieldset;
      this._isMultiple = isMultiple;
      this._open = false;

      // Scope hidden-input queries to this instance. closest("fieldset")
      // can resolve to a fieldset shared with another combobox; the
      // data-combobox-for match keeps reads/writes to our own inputs.
      this._instanceFor = `[data-combobox-for="${CSS.escape(wrapper.id)}"]`;
      this._valueSelector = `input[data-combobox-value]${this._instanceFor}`;
      this._anySelector =
        `input[data-combobox-value]${this._instanceFor}, input[data-combobox-empty]${this._instanceFor}`;

      this._input = wrapper.querySelector("input[type='text']");
      this._toggleBtn = wrapper.querySelector("[data-toggle]");
      this._clearBtn = wrapper.querySelector("[data-clear]");
      this._listbox = wrapper.querySelector("[role='listbox']");
      this._noMatchesEl = wrapper.querySelector("[data-no-matches]");
      this._originalPlaceholder = wrapper.dataset.placeholder || "";

      // Async mode: options come from the server via push_options/4 instead of
      // a static list. The hook debounces typing into a pushEvent and rebuilds
      // the <li>s from the response; selection still works exactly the same.
      this._onSearchEvent = wrapper.dataset.onSearch;
      this._isAsync = !!this._onSearchEvent;
      this._minLength = parseInt(wrapper.dataset.minLengthTerm || "2", 10);
      this._optionTemplate = this._listbox.querySelector("[data-option-template]");
      this._loadingEl = wrapper.querySelector("[data-loading]");
      this._minHintEl = wrapper.querySelector("[data-min-length-hint]");
      this._searchingEl = wrapper.querySelector("[data-searching]");
      this._debounceTimer = null;

      const hidden = fieldset.querySelector(this._anySelector);
      this._inputName = hidden ? hidden.name : "";

      // The option list is behind phx-update="ignore", so the hook owns the
      // checkmark. Seed it from the current selection on mount (sync mode; in
      // async there are no options yet — marks apply when results arrive).
      if (isMultiple) this._syncMarks();

      if (this._isAsync) {
        this.handleEvent("combobox-options:" + wrapper.id, ({ options, term }) => {
          // Drop a stale response: the user has typed past this search.
          if (term !== this._input.value.trim()) return;
          this._rebuildAsyncOptions(options);
          this._loadingEl?.classList.add("hidden");
          this._setHint(options.length === 0 ? "no_matches" : null);
          if (this._open) this._position();
        });
      }

      // combobox-nav owns keyboard nav, aria-activedescendant, and the
      // bubbling `combobox-commit` event (mouse click or keyboard Enter,
      // which it routes through a synthetic option click). We own open/close,
      // positioning, filtering, and the hidden-input bridge, and commit on
      // its event — it fires for picked and unpicked options alike (only
      // [aria-disabled] is skipped), so click-to-deselect works.
      this._combobox = new Combobox(this._input, this._listbox);

      this._onCommit = (e) => this._commit(e.target);
      this._listbox.addEventListener("combobox-commit", this._onCommit);

      // Keep the input focused when pressing inside the listbox or on the
      // chevron/clear buttons; preventing mousedown's default stops the
      // blur, so the dropdown does not close out from under the click.
      this._preventBlur = (e) => e.preventDefault();
      this._listbox.addEventListener("mousedown", this._preventBlur);
      this._toggleBtn?.addEventListener("mousedown", this._preventBlur);
      this._clearBtn?.addEventListener("mousedown", this._preventBlur);

      this._onFocus = () => {
        this._input.placeholder = this._originalPlaceholder;
        this._input.removeAttribute("data-selected");
        if (this._clearBtn) this._clearBtn.classList.add("hidden");
        this._openList();
      };
      this._input.addEventListener("focus", this._onFocus);

      this._onToggle = () => {
        if (this._open) {
          this._closeList();
        } else {
          this._input.focus();
          this._openList();
        }
      };
      this._toggleBtn?.addEventListener("click", this._onToggle);

      this._onKeydown = (e) => {
        if (e.key === "Escape") {
          if (this._open) {
            e.preventDefault();
            e.stopPropagation();
            this._closeList();
          }
          return;
        }
        if (e.key === "Enter") {
          // Block the form submit while the list is open; combobox-nav
          // still commits the active option (preventDefault does not stop
          // propagation, so its handler runs).
          if (this._open) e.preventDefault();
          return;
        }
        if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !this._open) {
          e.preventDefault();
          this._openList();
          this._combobox.navigate(e.key === "ArrowDown" ? 1 : -1);
        }
      };
      this._input.addEventListener("keydown", this._onKeydown);

      this._onFocusOut = (e) => {
        if (this._wrapper.contains(e.relatedTarget)) return;
        this._closeList();
        if (!isMultiple && this._input.value === "") this._syncSingleHostFromAttrs();
      };
      this._wrapper.addEventListener("focusout", this._onFocusOut);

      this._onDocPointerDown = (e) => {
        if (this._wrapper.contains(e.target)) return;
        this._closeList();
      };

      this._onInput = (e) => {
        // This filter box has no name; the combobox value lives in the hidden inputs, not here. Left
        // alone, its input event bubbles to the enclosing form and fires a no-op phx-change per
        // keystroke. Picks still work: they dispatch on the hidden inputs, which reach the form.
        e.stopPropagation();
        this._openList();
        this._refresh();
      };
      this._input.addEventListener("input", this._onInput);
      // The box also fires a native change on blur; stop that for the same reason.
      this._stopChange = (e) => e.stopPropagation();
      this._input.addEventListener("change", this._stopChange);

      if (this._clearBtn) {
        this._onClear = () => {
          this._writeValues([]);
          this._applySingleDisplay(null);
          this._dispatchFormInput();
          this._closeList();
        };
        this._clearBtn.addEventListener("click", this._onClear);
      }

      if (isMultiple) {
        this._onFieldsetClick = (e) => {
          const remove = e.target.closest("[data-pill-remove]");
          if (remove) {
            const val = remove.dataset.value;
            const next = this._readValues().filter((v) => v !== val);
            this._writeValues(next);
            this._syncMarks(next);
            this._dispatchFormInput();
            return;
          }
          const clearAll = e.target.closest("[data-clear-all]");
          if (clearAll) {
            this._writeValues([]);
            this._syncMarks([]);
            this._dispatchFormInput();
          }
        };
        this._fieldset.addEventListener("click", this._onFieldsetClick);
      }

      // The "+N more" overflow popover is an <el-popover> that self-anchors
      // under its invoker, so the hook does not position it.
    },

    updated() {
      // The option list is behind phx-update="ignore", so the server never
      // patches it: the live filter, the checkmarks, and the keyboard cursor
      // all survive a patch untouched. autoUpdate keeps the open dropdown
      // positioned (it tracks the input's layout shifts), so we only re-assert
      // aria-expanded and restore the single-select label when not focused.
      if (this._open) this._input.setAttribute("aria-expanded", "true");
      if (document.activeElement !== this._input) this._syncSingleHostFromAttrs();
    },

    destroyed() {
      clearTimeout(this._debounceTimer);
      this._closeList();
      this._combobox?.destroy();
      this._input?.removeEventListener("focus", this._onFocus);
      this._input?.removeEventListener("keydown", this._onKeydown);
      this._input?.removeEventListener("input", this._onInput);
      this._input?.removeEventListener("change", this._stopChange);
      this._listbox?.removeEventListener("combobox-commit", this._onCommit);
      this._listbox?.removeEventListener("mousedown", this._preventBlur);
      this._toggleBtn?.removeEventListener("mousedown", this._preventBlur);
      this._toggleBtn?.removeEventListener("click", this._onToggle);
      this._clearBtn?.removeEventListener("mousedown", this._preventBlur);
      this._clearBtn?.removeEventListener("click", this._onClear);
      this._wrapper?.removeEventListener("focusout", this._onFocusOut);
      this._fieldset?.removeEventListener("click", this._onFieldsetClick);
    },

    // ---- open / close ----
    _openList() {
      if (this._open) return;
      this._open = true;
      if (this._listbox && !this._popoverOpen()) {
        this._listbox.showPopover?.();
      }
      this._input.setAttribute("aria-expanded", "true");
      this._combobox.start();
      document.addEventListener("pointerdown", this._onDocPointerDown, true);
      // Floating UI keeps the dropdown anchored to the input across scroll,
      // resize, and input/ancestor size changes. autoUpdate positions it
      // immediately and on every change, returning a cleanup we call on close.
      this._stopAutoUpdate = autoUpdate(this._input, this._listbox, () => this._position());
      // Show the right contents for the current term: sync filters the list,
      // async searches or shows the min-length hint (so first open isn't blank).
      this._refresh();
    },

    _closeList() {
      if (!this._open) return;
      this._open = false;
      this._combobox.stop();
      if (this._popoverOpen()) this._listbox.hidePopover?.();
      this._input.setAttribute("aria-expanded", "false");
      document.removeEventListener("pointerdown", this._onDocPointerDown, true);
      this._stopAutoUpdate?.();
      this._stopAutoUpdate = null;
      // Reset the search box on close so the next open starts fresh (every
      // close path: blur, Escape, outside-click, chevron). _refresh then
      // re-shows the full list (sync) or drops the fetched results + shows the
      // min-length hint (async), so stale options don't linger and reopen.
      this._input.value = "";
      this._refresh();
    },

    // Whether the listbox popover is open. The Popover API is the component's
    // one hard browser requirement; it is polyfilled app-wide by
    // @tailwindplus/elements (OddBird), but guard anyway so a browser missing
    // both native support and the polyfill fails soft (the dropdown no-ops)
    // instead of throwing on the invalid `:popover-open` selector.
    _popoverOpen() {
      try {
        return !!this._listbox?.matches(":popover-open");
      } catch {
        return false;
      }
    },

    // ---- positioning (Floating UI) ----
    // Anchor the dropdown to the input (not the full control row) so it
    // matches the input width with the chevron outside it. flip moves it
    // above when there's no room below; shift keeps it in the viewport; size
    // matches the input width. strategy "fixed" matches the popover top
    // layer. Only CSSOM writes (.style.*), never an inline style attribute,
    // which the app's CSP forbids.
    _position() {
      if (!this._input || !this._listbox) return;
      return computePosition(this._input, this._listbox, {
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [
          offset(4),
          flip({ padding: 8 }),
          shift({ padding: 8 }),
          size({
            apply: ({ rects, availableHeight, elements }) => {
              elements.floating.style.width = `${rects.reference.width}px`;
              // Shrink to the room the viewport leaves, capped at the listbox max.
              elements.floating.style.maxHeight = `${Math.min(availableHeight, 240)}px`;
            },
          }),
        ],
      }).then(({ x, y }) => {
        this._listbox.style.left = `${x}px`;
        this._listbox.style.top = `${y}px`;
      });
    },

    // ---- selection ----
    _commit(option) {
      if (!option || option.getAttribute("role") !== "option") return;
      const value = option.dataset.value;
      if (value == null) return;
      const label = option.querySelector("[data-label]")?.textContent.trim() || value;

      const current = this._readValues();
      // Multi toggle shares the deselect path with the pill X; single
      // replaces, and the clear button empties it.
      const next = this._isMultiple
        ? current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value]
        : [value];
      this._writeValues(next);

      if (this._isMultiple) {
        // Keep the typed term and filtered list so several matches can be
        // added in a row. Toggle the checkmark on the client (the options are
        // behind phx-update="ignore"; phx-change re-renders only the pills),
        // so the keyboard cursor and the filter are left untouched — no
        // re-assert, no blink. The pills update live from the round-trip.
        this._syncMarks(next);
        this._dispatchFormInput();
        this._input.focus();
      } else {
        this._input.value = "";
        this._applySingleDisplay(label);
        this._dispatchFormInput();
        this._closeList();
      }
    },

    // ---- filtering (combobox-nav skips [hidden] options) ----
    _filter() {
      const term = this._input.value.trim().toLowerCase();
      let visible = 0;
      this._listbox.querySelectorAll("[role='option']").forEach((opt) => {
        const text = (opt.textContent || "").trim().toLowerCase();
        const match = term === "" || text.includes(term);
        opt.hidden = !match;
        if (match) visible++;
      });
      this._noMatchesEl?.classList.toggle("hidden", !(visible === 0 && term !== ""));
      this._combobox.clearSelection();
    },

    // ---- picked-state (client-owned; options sit behind phx-update="ignore") ----
    // Flip aria-checked + the checkmark on the picked options. Defaults to the
    // hidden inputs; pass an explicit list to reflect a pick before reading back.
    _syncMarks(values) {
      if (!this._isMultiple) return;
      const set = new Set(values || this._readValues());
      this._listbox.querySelectorAll("[role='option']").forEach((opt) => {
        const picked = set.has(opt.dataset.value);
        opt.setAttribute("aria-checked", picked ? "true" : "false");
        opt.querySelector("[data-check]")?.classList.toggle("hidden", !picked);
      });
    },

    // Show the dropdown contents for the current term. The one seam between
    // sync and async: sync filters the static list client-side; async fires a
    // (debounced) server search or shows the min-length hint.
    _refresh() {
      if (this._isAsync) this._asyncSearch();
      else this._filter();
    },

    // ---- async (options fetched server-side via push_options/4) ----
    _asyncSearch() {
      const term = this._input.value.trim();
      clearTimeout(this._debounceTimer);
      if (term.length < this._minLength) {
        this._loadingEl?.classList.add("hidden");
        this._clearAsyncOptions();
        this._setHint("min_length");
        return;
      }
      // Keep any current results visible while the new search debounces; only
      // show the "Searching..." row when there's nothing to keep, so the
      // dropdown isn't blank on the first search.
      this._loadingEl?.classList.remove("hidden");
      const hasOptions = this._listbox.querySelector("[role='option']") != null;
      this._setHint(hasOptions ? null : "searching");
      this._debounceTimer = setTimeout(() => {
        this.pushEvent(this._onSearchEvent, { id: this._wrapper.id, term });
      }, 300);
    },

    _clearAsyncOptions() {
      this._listbox.querySelectorAll("[role='option']").forEach((o) => o.remove());
    },

    // Clone the template per result; re-apply checkmarks since a rebuild drops them.
    _rebuildAsyncOptions(options) {
      this._clearAsyncOptions();
      options.forEach((opt, idx) => {
        const li = this._optionTemplate.content.firstElementChild.cloneNode(true);
        li.id = `${this._wrapper.id}-opt-${idx}`;
        li.dataset.value = opt.value;
        li.querySelector("[data-label]").textContent = opt.label;
        this._listbox.appendChild(li);
      });
      this._syncMarks();
    },

    // At most one hint visible; pass null to hide both.
    _setHint(which) {
      this._minHintEl?.classList.toggle("hidden", which !== "min_length");
      this._noMatchesEl?.classList.toggle("hidden", which !== "no_matches");
      this._searchingEl?.classList.toggle("hidden", which !== "searching");
    },

    // ---- hidden-input model ----
    _readValues() {
      return Array.from(this._fieldset.querySelectorAll(this._valueSelector))
        .map((h) => h.value)
        .filter((v) => v !== "");
    },

    _writeValues(values) {
      this._fieldset.querySelectorAll(this._anySelector).forEach((h) => h.remove());

      if (values.length === 0) {
        // Multi: empty placeholder so the form param is sent as [].
        // Single: a single empty-value input.
        const ph = document.createElement("input");
        ph.hidden = true;
        ph.name = this._inputName;
        ph.value = "";
        ph.setAttribute("data-combobox-for", this._wrapper.id);
        if (this._isMultiple) ph.setAttribute("data-combobox-empty", "");
        else ph.setAttribute("data-combobox-value", "");
        this._fieldset.appendChild(ph);
        return;
      }

      for (const v of values) {
        const h = document.createElement("input");
        h.hidden = true;
        h.name = this._inputName;
        h.value = v;
        h.setAttribute("data-combobox-value", "");
        h.setAttribute("data-combobox-for", this._wrapper.id);
        this._fieldset.appendChild(h);
      }
    },

    _dispatchFormInput() {
      const target = this._fieldset.querySelector(this._anySelector);
      if (target) target.dispatchEvent(new Event("input", { bubbles: true }));
    },

    // ---- single-select display ----
    _syncSingleHostFromAttrs() {
      if (this._isMultiple) return;
      const value = this._wrapper.dataset.selectedValue || "";
      const label = this._wrapper.dataset.selectedLabel || "";
      this._applySingleDisplay(value && label ? label : null);
    },

    _applySingleDisplay(label) {
      if (label) {
        this._input.placeholder = label;
        this._input.setAttribute("data-selected", "");
        this._clearBtn?.classList.remove("hidden");
      } else {
        this._input.placeholder = this._originalPlaceholder;
        this._input.removeAttribute("data-selected");
        this._clearBtn?.classList.add("hidden");
      }
    },
  };
